import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import httpClient from "services/network/httpClient";
import axios from "axios";
import { useEffect, useState } from "react";
import CustomSelect from "components/Select";
import appConfig from "services/config";

interface TotalSummaryFormProps {
    fromDate: string;
    toDate: string;
    manager: string;
    employee: string;
}

interface Option {
    label: string;
    value: number;
}

interface ManagerProps {
    id: number;
    firstName: string;
    lastName: string;
}

interface AssociatedEmployeesProps {
    id: number;
    firstName: string;
    lastName: string;
}

const schema = yup.object({
    fromDate: yup.string().optional(),
    toDate: yup.string().optional(),
    manager: yup.string().optional(),
    employee: yup.string().optional(),
});

const TotalSummary = () => {

    const [listOfManager, setListOfManager] = useState<Option[]>([]);
    const [associatedEmployeeData, setAssociatedEmployeeData] = useState<Option[]>([]);
    const [selectedManager, setSelectedManager] = useState<Option>();

    const today = new Date().toISOString().split("T")[0];

    const firstOfMonth = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
    ).toISOString().split("T")[0];

    const methods = useForm<TotalSummaryFormProps>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            fromDate: firstOfMonth,
            toDate: today,
            manager: "",
            employee: "",
        },
    });

    const { handleSubmit } = methods;

    // ---------- GET MANAGERS ----------
    const getManagerData = () => {
        httpClient.get("/employees/managers")
            .then((response: any) => {

                if (response.success) {
                    const managers = response.value.map((manager: ManagerProps) => ({
                        label: `${manager.firstName} ${manager.lastName}`,
                        value: manager.id
                    }));

                    setListOfManager(managers);
                }

            }).catch((error) => {
                console.error(error);
            });
    };

    // ---------- GET EMPLOYEES ----------
    const getAssociatedEmployees = () => {

        const api = selectedManager
            ? `/employees/managers/${selectedManager.value}`
            : `/employees/all?page=1&size=2000`;

        httpClient.get(api)
            .then((response: any) => {

                if (response.success) {

                    const employees = selectedManager
                        ? response.value
                        : response.value.contents;

                    const mapped = employees.map((emp: AssociatedEmployeesProps) => ({
                        label: `${emp.firstName} ${emp.lastName}`,
                        value: emp.id
                    }));

                    setAssociatedEmployeeData(mapped);
                }

            }).catch((error) => {
                console.error(error);
            });
    };

    useEffect(() => {
        getManagerData();
    }, []);

    useEffect(() => {
        getAssociatedEmployees();
    }, [selectedManager]);

    // ---------- FORM SUBMIT ----------
    const onSubmit = async (data: TotalSummaryFormProps) => {
        console.log(data);
        const token = localStorage.getItem("accessToken");
        const baseUrl = appConfig.getBaseUrl();
        console.log("Base URL : ", baseUrl);

        await axios.get(
            `${baseUrl}/reports/demos`,
            {
                params: {
                    fromDate: data.fromDate,
                    toDate: data.toDate,
                    managerId: data.manager,
                    ...(data.employee && { empIds: data.employee })
                },
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then((response) => {

                const url = window.URL.createObjectURL(
                    new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    })
                );

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `report-${Date.now()}.xlsx`);
                document.body.appendChild(link);
                link.click();

                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    return (
        <FormProvider {...methods}>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 w-full max-w-xl">

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

                    <h2 className="text-2xl font-semibold text-gray-700 text-center">
                        Generate Summary Report
                    </h2>

                    <div className="grid grid-cols-2 gap-6">

                        {/* FROM DATE */}
                        <div className="flex flex-col gap-1">

                            <Input name="fromDate" type="date" label="From Date" />
                        </div>

                        {/* TO DATE */}
                        <div className="flex flex-col gap-1">

                            <Input name="toDate" type="date" label='To Date' />
                        </div>

                        {/* MANAGER */}
                        <CustomSelect
                            name="manager"
                            label="Manager"
                            placeholder="Select Manager"
                            options={listOfManager}
                            onChange={(value: Option) => setSelectedManager(value)}
                        />

                        {/* EMPLOYEE */}
                        <CustomSelect
                            name="employee"
                            label="Employee"
                            placeholder="Select Employee"
                            options={associatedEmployeeData}
                        />

                    </div>

                    <div className="flex justify-center pt-4">
                        <button
                            type="submit"
                            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                            Generate Document
                        </button>
                    </div>

                </form>

            </div>

        </FormProvider>
    );
};

export default TotalSummary;